import React, { useState, useRef, useEffect } from 'react';
import { Search, Plus, Upload, Filter, MoreHorizontal, X, FileText, Image as ImageIcon, Download, Eye, Edit, Trash, FileUp, FileDown, User, Mail, Phone, Calendar, BookOpen, GraduationCap, MapPin, BadgeCheck, QrCode, Save, AlertCircle } from 'lucide-react';
import { addStudent, getStudents, deleteStudent, editStudent } from '../../services/studentService';
import { toast } from 'react-toastify';

export const Students: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [viewStudent, setViewStudent] = useState<any | null>(null);
    const [actionMenuOpen, setActionMenuOpen] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [calibrated, setCalibrated] = useState(false);
    const [loadingList, setLoadingList] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    // Profile Edit State
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [profileEditForm, setProfileEditForm] = useState<any>(null);
    const profilePhotoRef = useRef<HTMLInputElement>(null);

    const [statusFilter, setStatusFilter] = useState('All');
    const [yearFilter, setYearFilter] = useState('All');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const photoInputRef = useRef<HTMLInputElement>(null);

    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '', phoneNumber: '', courseOfStudy: '', yearOfStudy: '', programStartDate: '', programStopDate: '', photo: null as string | null
    });
    const [students, setStudents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // DELETE CONFIRMATION STATES
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [studentToDelete, setStudentToDelete] = useState<any | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    useEffect(() => {
        const loadStudents = async () => {
            try {
                setLoading(true);
                setLoadingList(true);
                if (students.length === 0) {
                    try {
                        const cached = localStorage.getItem('students_cache');
                        if (cached) {
                            const items = JSON.parse(cached) || [];
                            const mapped = items.map((s: any) => ({
                                id: s.id,
                                name: `${s.first_name || ''} ${s.last_name || ''}`.trim(),
                                studentId: s.student_id || s.id,
                                course: s.course_name,
                                year: s.year_of_study,
                                status: s.status || (s.certificates && s.certificates.length > 0 ? 'Certified' : 'Not Certified'),
                                email: s.email,
                                phone: s.phone_number,
                                createdAt: s.created_at,
                            }));
                            if (mapped.length > 0) setStudents(mapped);
                        }
                    } catch {}
                }
                const data = await getStudents();
                const mapped = (data.students || []).map((s: any) => ({
                    id: s.id,
                    name: `${s.first_name || ''} ${s.last_name || ''}`.trim(),
                    studentId: s.student_id || s.id,
                    course: s.course_name,
                    year: s.year_of_study,
                    status: s.status || (s.certificates && s.certificates.length > 0 ? 'Certified' : 'Not Certified'),
                    email: s.email,
                    phone: s.phone_number,
                    createdAt: s.created_at,
                }));
                setStudents(mapped);
                setTotal(mapped.length);
                try { localStorage.setItem('students_cache', JSON.stringify(data.students)); } catch {}
            } catch (err) {
                setError('Failed to fetch students. Please try again.');
            } finally {
                setLoading(false);
                setLoadingList(false);
            }
        };
        loadStudents();
    }, []);

    const filteredStudents = students.filter((s: any) => {
        const q = (searchQuery || '').toLowerCase();
        const name = String(s?.name || `${s?.first_name || ''} ${s?.last_name || ''}` || '').toLowerCase();
        const id = String(s?.studentId || s?.id || '').toLowerCase();
        const course = String(s?.course || s?.course_name || '').toLowerCase();
        const matchesSearch = name.includes(q) || id.includes(q) || course.includes(q);
        const matchesStatus = statusFilter === 'All' || String(s?.status || '').toLowerCase() === String(statusFilter || '').toLowerCase();
        const matchesYear = yearFilter === 'All' || String(s?.year || s?.year_of_study || '').toLowerCase() === String(yearFilter || '').toLowerCase();
        return matchesSearch && matchesStatus && matchesYear;
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredStudents.slice(indexOfFirstItem, indexOfLastItem);
    const totalPagesComputed = Math.max(1, Math.ceil(filteredStudents.length / itemsPerPage || 1));

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPagesComputed) setCurrentPage(newPage);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, photo: reader.result as string }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddStudent = async () => {
        const requiredFields = ["firstName", "lastName"];
        for (const field of requiredFields) {
            if (!formData[field]) {
                toast.error(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
                return;
            }
        }

        try {
            const studentPayload = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                courseName: formData.courseOfStudy,
                yearOfStudy: formData.yearOfStudy,
                programStartDate: formData.programStartDate,
                programEndDate: formData.programStopDate,
                photoUrl: formData.photo,
            };

            await addStudent(studentPayload);
            toast.success("Student added successfully");
            setIsModalOpen(false);
            setFormData({
                firstName: '', lastName: '', email: '', phoneNumber: '', courseOfStudy: '',
                yearOfStudy: '', programStartDate: '', programStopDate: '', photo: null
            });

            const data = await getStudents();
            const mapped = (data.students || []).map((s: any) => ({
                id: s.id,
                name: `${s.first_name || ''} ${s.last_name || ''}`.trim(),
                studentId: s.student_id || s.id,
                course: s.course_name,
                year: s.year_of_study,
                status: s.status || (s.certificates && s.certificates.length > 0 ? 'Certified' : 'Not Certified'),
                email: s.email,
                phone: s.phone_number,
                createdAt: s.created_at,
            }));
            setStudents(mapped);
            setTotal(mapped.length);
        } catch (error: any) {
            toast.error(error?.message || "Failed to add student");
        }
    };

    // DELETE MODAL HANDLERS
    const openDeleteModal = (student: any, e: React.MouseEvent) => {
        e.stopPropagation();
        setStudentToDelete(student);
        setDeleteConfirmText('');
        setDeleteModalOpen(true);
        setActionMenuOpen(null);
    };

    const confirmAndDelete = async () => {
        if (!studentToDelete || deleteConfirmText.toLowerCase() !== 'delete') return;

        try {
            await deleteStudent(studentToDelete.id);
            setStudents(prev => prev.filter(s => s.id !== studentToDelete.id));
            toast.success("Student deleted successfully");
        } catch (error: any) {
            toast.error(error?.message || "Failed to delete student");
        } finally {
            setDeleteModalOpen(false);
            setStudentToDelete(null);
            setDeleteConfirmText('');
        }
    };

    const handleToggleAll = (checked: boolean) => {
        setSelectAll(checked);
        if (checked) {
            setSelectedIds(filteredStudents.map((s: any) => s.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleToggleOne = (id: number, checked: boolean) => {
        setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
        if (!checked) setSelectAll(false);
    };

    const handleBulkDownload = () => {
        if (selectedIds.length === 0) return;
        const selected = students.filter(s => selectedIds.includes(s.id));
        const headers = ["Name", "Course", "Student ID", "Year", "Status", "Email", "Phone"];
        const rows = selected.map(s => [s.name, s.course, s.studentId, s.year, s.status, s.email, s.phone]);
        const csv = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(r => `"${r.join('","')}"`).join("\n");
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csv));
        link.setAttribute("download", "students-selected.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleImportClick = () => { if (fileInputRef.current) fileInputRef.current.click(); };
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && !["text/csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"].includes(file.type)) {
            toast.error("Please upload a valid CSV or Excel file.");
            return;
        }
        toast.success(`File "${file?.name}" ready for import!`);
    };

    const handleExport = () => {
        const headers = ["Name", "Course", "Student ID", "Year", "Status", "Email", "Phone"];
        const rows = students.map(s => [s.name, s.course, s.studentId, s.year, s.status, s.email, s.phone]);
        const csv = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(r => `"${r.join('","')}"`).join("\n");
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csv));
        link.setAttribute("download", "students.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleActionMenu = (id: number) => {
        setActionMenuOpen(prev => prev === id ? null : id);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!(event.target as Element).closest('.action-menu-container')) {
                setActionMenuOpen(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleViewStudent = (student: any) => {
        setViewStudent(student);
        setProfileEditForm({
            ...student,
            name: student.name,
            course: student.course,
            year: student.year,
            status: student.status,
            studentId: student.studentId,
            email: student.email || '',
            phone: student.phone || '',
            programStartDate: student.programStartDate || '',
            programEndDate: student.programEndDate || '',
            photo: student.photo || null
        });
        setIsEditingProfile(false);
        setActionMenuOpen(null);
    };

    const handleProfileEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setProfileEditForm({ ...profileEditForm, [e.target.name]: e.target.value });
    };

    const handleProfilePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileEditForm((prev: any) => ({ ...prev, photo: reader.result as string }));
            };
            reader.readAsDataURL(file);
        }
    };

    const saveProfileChanges = async () => {
        try {
            const payload = {
                firstName: profileEditForm.name.split(" ")[0] || "",
                lastName: profileEditForm.name.split(" ").slice(1).join(" ") || "",
                email: profileEditForm.email,
                phoneNumber: profileEditForm.phone,
                courseName: profileEditForm.course,
                yearOfStudy: profileEditForm.year,
                programStartDate: profileEditForm.programStartDate,
                programEndDate: profileEditForm.programEndDate,
                photoUrl: profileEditForm.photo,
            };

            await editStudent(profileEditForm.id, payload);
            setStudents(prev =>
                prev.map(s => (s.id === profileEditForm.id ? { ...s, ...profileEditForm } : s))
            );
            toast.success("Student updated successfully");
            setIsEditingProfile(false);
        } catch (error: any) {
            toast.error(error?.message || "Failed to update student");
        }
    };

    return (
        <div className="space-y-6">
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv,.xlsx,.xls" className="hidden" />

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Students</h2>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mt-1">Manage student registrations, courses, and verify records.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 w-4 h-4" />
                        <input type="text" placeholder="Search for student..." value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }} className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm w-full md:w-64 focus:ring-primary focus:border-primary text-gray-800 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400" />
                    </div>
                    <button onClick={handleImportClick} className="bg-white dark:bg-gray-800 border border-primary text-primary dark:text-indigo-400 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-50 dark:hover:bg-gray-700 flex items-center justify-center gap-2 whitespace-nowrap shadow-sm transition-colors">
                        <FileUp className="w-4 h-4" /> Import CSV
                    </button>
                    <button onClick={() => setIsModalOpen(true)} className="bg-primary hover:bg-indigo-800 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 whitespace-nowrap shadow-sm">
                        <Plus className="w-4 h-4" /> Add Student
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden min-h-[400px] flex flex-col transition-colors">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <button onClick={handleExport} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                            <FileDown className="w-4 h-4" /> Export
                        </button>
                        {/* <button onClick={() => setReloadKey(k => k + 1)} className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-primary text-primary rounded-md text-sm font-medium hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors">
                            Refresh
                        </button> */}
                        <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-3 py-1.5 border rounded-md text-sm font-medium transition-colors ${showFilters ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                            <Filter className="w-3 h-3" /> Filters
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleBulkDownload}
                            disabled={selectedIds.length < 2}
                            className={`px-3 py-1.5 border rounded-md text-xs font-semibold ${
                                selectedIds.length >= 2
                                    ? 'border-primary text-white bg-primary hover:bg-indigo-900 dark:hover:bg-gray-700'
                                    : 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-100 dark:bg-gray-700'
                            }`}
                        >
                            Download Selected
                        </button>
                    </div>
                </div>

                {showFilters && (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm">
                            <option value="All">Status: All</option>
                            <option value="Verified">Verified</option>
                            <option value="Unverified">Unverified</option>
                        </select>
                        <select value={yearFilter} onChange={(e) => { setYearFilter(e.target.value); setCurrentPage(1); }} className="border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm">
                            <option value="All">Year: All</option>
                            <option value="2023">2023</option>
                            <option value="2024">2024</option>
                            <option value="2025">2025</option>
                        </select>
                    </div>
                )}

                <div className="overflow-x-auto flex-grow">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 uppercase text-xs font-bold">
                            <tr>
                                <th className="px-6 py-3 w-4"><input type="checkbox" checked={selectAll} onChange={(e) => handleToggleAll(e.target.checked)} className="rounded border-gray-400 text-primary focus:ring-primary bg-white" /></th>
                                <th className="px-6 py-3">Name</th>
                                <th className="px-6 py-3">Student ID</th>
                                <th className="px-6 py-3">Course</th>
                                <th className="px-6 py-3">Year</th>
                                <th className="px-6 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {currentItems.length > 0 ? currentItems.map((student) => (
                                <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4"><input type="checkbox" checked={selectedIds.includes(student.id)} onChange={(e) => handleToggleOne(student.id, e.target.checked)} className="rounded border-gray-400 text-primary focus:ring-primary bg-white" /></td>
                                    <td className="px-6 py-4 font-semibold text-primary dark:text-indigo-400 cursor-pointer hover:underline" onClick={() => handleViewStudent(student)}>{student.name}</td>
                                    <td className="px-6 py-4 font-mono text-xs text-gray-700 dark:text-gray-400 font-medium">{student.studentId}</td>
                                    <td className="px-6 py-4 text-gray-800 dark:text-gray-300">{student.course}</td>
                                    <td className="px-6 py-4 text-gray-800 dark:text-gray-300">{student.year}</td>
                                    <td className="px-6 py-4 action-menu-container relative">
                                        <button onClick={(e) => { e.stopPropagation(); toggleActionMenu(student.id); }} className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white p-1">
                                            <MoreHorizontal className="w-5 h-5" />
                                        </button>
                                        {actionMenuOpen === student.id && (
                                            <div className="absolute right-8 top-0 mt-2 w-32 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                                                <div className="py-1">
                                                    <button onClick={() => handleViewStudent(student)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left font-medium">
                                                        <Eye className="w-4 h-4" /> View
                                                    </button>
                                                    <button onClick={() => { handleViewStudent(student); setIsEditingProfile(true); }} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left font-medium">
                                                        <Edit className="w-4 h-4" /> Edit
                                                    </button>
                                                    <button onClick={(e) => openDeleteModal(student, e)} className="flex items-center gap-2 px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left font-medium">
                                                        <Trash className="w-4 h-4" /> Delete
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={6} className="text-center py-8 text-gray-500">No students found</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="p-4 border-t flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800/50">
                    <span>
                        {filteredStudents.length === 0
                            ? 'Showing 0-0 of 0'
                            : `Showing ${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, filteredStudents.length)} of ${filteredStudents.length}`}
                    </span>
                    <div className="flex gap-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded disabled:opacity-50">Previous</button>
                        {Array.from({ length: totalPagesComputed }, (_, i) => i + 1).map(p => (
                            <button key={p} onClick={() => handlePageChange(p)} className={`px-3 py-1 rounded ${currentPage === p ? 'bg-primary text-white' : 'border'}`}>{p}</button>
                        ))}
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPagesComputed} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                    </div>
                </div>
            </div>

           {/* DELETE CONFIRMATION MODAL – WITH X CLOSE BUTTON */}
            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 border relative">
                        {/* Close X Button */}
                        <button
                            onClick={() => {
                                setDeleteModalOpen(false);
                                setStudentToDelete(null);
                                setDeleteConfirmText('');
                            }}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <div className="flex items-center gap-3 text-red-600 mb-4">
                            <AlertCircle className="w-8 h-8" />
                            <h3 className="text-xl font-bold">Delete Student?</h3>
                        </div>

                        <p className="text-gray-700 dark:text-gray-300 mb-6">
                            Are you sure you want to delete the student <strong>{studentToDelete?.name}</strong>?<br />
                            <span className="font-bold text-red-600">This action cannot be undone.</span>
                        </p>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Type <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">delete</span> to confirm
                            </label>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="delete"
                                autoFocus
                            />
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setDeleteModalOpen(false);
                                    setStudentToDelete(null);
                                    setDeleteConfirmText('');
                                }}
                                className="px-5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAndDelete}
                                disabled={deleteConfirmText.toLowerCase() !== 'delete'}
                                className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                            >
                                <Trash className="w-4 h-4" /> Proceed & Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* VIEW / EDIT STUDENT MODAL */}
            {viewStudent && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col md:flex-row max-h-[90vh]">
                        {/* Left: ID Card */}
                        <div className="w-full md:w-1/3 bg-primary p-8 text-white relative flex flex-col items-center text-center justify-between">
                            <div className="z-10 w-full flex flex-col items-center">
                                <div className="w-12 h-12 rounded-full bg-white mb-4 shadow-lg overflow-hidden flex items-center justify-center">
                                    <img src="/logo.png" alt="Logo" className="w-full h-full object-contain p-1" />
                                </div>
                                <h3 className="text-sm font-bold tracking-widest uppercase opacity-80 mb-6">Student Identity</h3>

                                <div className="w-32 h-32 rounded-xl border-4 border-white/30 bg-white shadow-xl overflow-hidden mb-4 relative group">
                                    {isEditingProfile ? (
                                        <div onClick={() => profilePhotoRef.current?.click()} className="w-full h-full flex flex-col items-center justify-center bg-gray-200 text-gray-600 cursor-pointer hover:bg-gray-300 transition-colors">
                                            {profileEditForm.photo ? (
                                                <img src={profileEditForm.photo} alt="Preview" className="w-full h-full object-cover" />
                                            ) : (
                                                <>
                                                    <ImageIcon className="w-8 h-8 mb-1" />
                                                    <span className="text-[10px]">Change Photo</span>
                                                </>
                                            )}
                                            <input type="file" ref={profilePhotoRef} onChange={handleProfilePhotoChange} accept="image/*" className="hidden" />
                                        </div>
                                    ) : profileEditForm.photo ? (
                                        <img src={profileEditForm.photo} alt={profileEditForm.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <User className="w-full h-full p-6 text-gray-300 bg-gray-100" />
                                    )}
                                    <div className="absolute bottom-0 w-full bg-black/50 text-[10px] py-1 text-center backdrop-blur-sm">
                                        {profileEditForm.year}
                                    </div>
                                </div>

                                {isEditingProfile ? (
                                    <input name="name" value={profileEditForm.name} onChange={handleProfileEditChange} className="text-xl font-bold leading-tight mb-2 text-center bg-white/20 border border-white/30 rounded px-2 py-1 text-white w-full placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white" />
                                ) : (
                                    <h2 className="text-xl font-bold leading-tight mb-1">{profileEditForm.name}</h2>
                                )}

                                {isEditingProfile ? (
                                    <input name="course" value={profileEditForm.course} onChange={handleProfileEditChange} className="text-xs text-indigo-200 font-medium mb-4 text-center bg-white/20 border border-white/30 rounded px-2 py-1 w-full placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white" />
                                ) : (
                                    <p className="text-xs text-indigo-200 font-medium mb-4">{profileEditForm.course}</p>
                                )}

                                {isEditingProfile ? (
                                    <select name="status" value={profileEditForm.status} onChange={handleProfileEditChange} className="px-2 py-1 rounded text-xs font-bold border bg-white/20 border-white/30 text-white w-fit focus:outline-none focus:ring-1 focus:ring-white">
                                        <option value="Verified" className="text-black">Verified</option>
                                        <option value="Unverified" className="text-black">Unverified</option>
                                    </select>
                                ) : (
                                    <div className={`px-3 py-1 rounded-full text-xs font-bold border ${profileEditForm.status === 'Verified' ? 'bg-green-500/20 border-green-400 text-green-100' : 'bg-yellow-500/20 border-yellow-400 text-yellow-100'}`}>
                                        {profileEditForm.status} Status
                                    </div>
                                )}
                            </div>

                            <div className="z-10 w-full mt-8 border-t border-white/20 pt-4">
                                <div className="flex justify-between items-center text-xs opacity-70 mb-2">
                                    <span>ID Number</span>
                                    <QrCode className="w-4 h-4" />
                                </div>
                                <div className="font-mono text-sm tracking-wider bg-black/20 p-2 rounded text-center">
                                    {profileEditForm.studentId}
                                </div>
                            </div>
                        </div>

                        {/* Right: Details */}
                        <div className="w-full md:w-2/3 p-8 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Academic Overview</h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Comprehensive student record</p>
                                </div>
                                <button onClick={() => { setViewStudent(null); setIsEditingProfile(false); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400"><Calendar className="w-4 h-4" /></div>
                                        <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Year of Entry</span>
                                    </div>
                                    {isEditingProfile ? (
                                        <input name="year" value={profileEditForm.year} onChange={handleProfileEditChange} className="text-lg font-bold text-gray-900 dark:text-white w-full border-b border-gray-300 dark:border-gray-600 bg-transparent focus:outline-none focus:border-primary" />
                                    ) : (
                                        <p className="text-lg font-bold text-gray-900 dark:text-white pl-1">{profileEditForm.year}</p>
                                    )}
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-purple-600 dark:text-purple-400"><BookOpen className="w-4 h-4" /></div>
                                        <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Program</span>
                                    </div>
                                    <p className="text-sm font-bold text-gray-900 dark:text-white pl-1 truncate">{profileEditForm.course}</p>
                                </div>
                            </div>

                            <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
                                <BadgeCheck className="w-4 h-4" /> Contact Information
                            </h4>
                            <div className="space-y-3 mb-8">
                                <div className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                                    <Mail className="w-5 h-5 text-gray-400 mr-3" />
                                    <div className="w-full">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Email Address</p>
                                        {isEditingProfile ? (
                                            <input name="email" value={profileEditForm.email} onChange={handleProfileEditChange} className="text-sm font-medium text-gray-900 dark:text-white w-full border-b border-gray-300 dark:border-gray-600 bg-transparent focus:outline-none focus:border-primary" />
                                        ) : (
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">{profileEditForm.email || 'N/A'}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                                    <Phone className="w-5 h-5 text-gray-400 mr-3" />
                                    <div className="w-full">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Phone Number</p>
                                        {isEditingProfile ? (
                                            <input name="phone" value={profileEditForm.phone} onChange={handleProfileEditChange} className="text-sm font-medium text-gray-900 dark:text-white w-full border-b border-gray-300 dark:border-gray-600 bg-transparent focus:outline-none focus:border-primary" />
                                        ) : (
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">{profileEditForm.phone || 'N/A'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                {isEditingProfile ? (
                                    <>
                                        <button onClick={() => setIsEditingProfile(false)} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                                            Cancel
                                        </button>
                                        <button onClick={saveProfileChanges} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-sm flex items-center gap-2">
                                            <Save className="w-4 h-4" /> Save Changes
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                                            Download Record
                                        </button>
                                        <button onClick={() => setIsEditingProfile(true)} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-indigo-800 transition-colors shadow-sm flex items-center gap-2">
                                            <Edit className="w-4 h-4" /> Edit Profile
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ADD STUDENT MODAL */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add Student</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-8">
                            <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                                <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-2">To add bulk students you can import the database below</p>
                                <a href="#" className="text-xs text-primary hover:underline mb-4 block font-semibold">Download Sample CSV File</a>
                                <button onClick={handleImportClick} className="flex items-center gap-2 px-4 py-2 border border-primary text-primary dark:text-indigo-400 bg-white dark:bg-gray-800 rounded-md text-sm font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors shadow-sm">
                                    <FileUp className="w-4 h-4" /> Import CSV File
                                </button>
                            </div>
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Input Student Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {['First Name', 'Last Name', 'Email', 'Phone Number', 'Course of Study', 'Year of Study'].map(label => {
                                    const name = label.toLowerCase().replace(/\s/g, '').replace('ofstudy', 'OfStudy').replace('firstname', 'firstName').replace('lastname', 'lastName').replace('phonenumber', 'phoneNumber').replace('yearofstudy', 'yearOfStudy');
                                    return (
                                        <div key={label}>
                                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{label} *</label>
                                            <input type={label === 'Email' ? 'email' : 'text'} name={name} value={(formData as any)[name]} onChange={handleInputChange} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-primary focus:border-primary text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400" placeholder={`Enter ${label}`} />
                                        </div>
                                    );
                                })}
                                <div><label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Program Start Date</label><input type="date" name="programStartDate" value={formData.programStartDate} onChange={handleInputChange} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-primary focus:border-primary text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700" /></div>
                                <div><label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Program Stop Date</label><input type="date" name="programStopDate" value={formData.programStopDate} onChange={handleInputChange} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-primary focus:border-primary text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700" /></div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Photo *</label>
                                    <div onClick={() => photoInputRef.current?.click()} className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors h-32 relative overflow-hidden">
                                        {formData.photo ? (
                                            <img src={formData.photo} alt="Preview" className="absolute inset-0 w-full h-full object-cover" />
                                        ) : (
                                            <>
                                                <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                                                <p className="text-xs text-gray-500 dark:text-gray-400 text-center font-medium">Click to upload photo</p>
                                            </>
                                        )}
                                        <input type="file" ref={photoInputRef} onChange={handlePhotoSelect} accept="image/*" className="hidden" />
                                    </div>
                                </div>
                            </div>
                            <div className="mt-8">
                                <button onClick={handleAddStudent} className="w-fit bg-primary text-white px-8 py-3 rounded-lg text-sm font-bold hover:bg-indigo-800 transition-all shadow-md">
                                    Add Student
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Students;
//                 <div className="flex gap-2">
//                     <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-800 dark:text-gray-200 font-medium transition-colors">Previous</button>
//                     {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
//                         <button key={page} onClick={() => handlePageChange(page)} className={`px-3 py-1 border rounded text-xs font-medium transition-colors ${currentPage === page ? 'bg-primary text-white border-primary' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}>{page}</button>
//                     ))}
//                     <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-800 dark:text-gray-200 font-medium transition-colors">Next</button>
//                 </div>
//             </div>
//         </div>

//         {/* Improved Student Profile Modal with Edit Capability */}
//         {viewStudent && (
//              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200 backdrop-blur-sm">
//              <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col md:flex-row max-h-[90vh]">
                 
//                  {/* Left Panel: Digital ID Card Style */}
//                  <div className="w-full md:w-1/3 bg-gradient-to-br from-indigo-700 to-darkblue p-8 text-white relative flex flex-col items-center text-center justify-between">
//                      <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white to-transparent"></div>
                     
//                      <div className="z-10 w-full flex flex-col items-center">
//                          <div className="w-12 h-12 rounded-full bg-white mb-4 shadow-lg overflow-hidden flex items-center justify-center">
//                             <img src="/public/logo.png" alt="Company Logo" className="w-full h-full object-contain p-1" />
//                          </div>
//                          <h3 className="text-sm font-bold tracking-widest uppercase opacity-80 mb-6">Student Identity</h3>
                         
//                          <div className="w-32 h-32 rounded-xl border-4 border-white/30 bg-white shadow-xl overflow-hidden mb-4 relative group">
//                             {isEditingProfile ? (
//                                 <div onClick={() => profilePhotoRef.current?.click()} className="w-full h-full flex flex-col items-center justify-center bg-gray-200 text-gray-600 cursor-pointer hover:bg-gray-300 transition-colors">
//                                     {profileEditForm.photo ? (
//                                         <img src={profileEditForm.photo} alt="Preview" className="w-full h-full object-cover" />
//                                     ) : (
//                                         <>
//                                             <ImageIcon className="w-8 h-8 mb-1" />
//                                             <span className="text-[10px]">Change Photo</span>
//                                         </>
//                                     )}
//                                     <input type="file" ref={profilePhotoRef} onChange={handleProfilePhotoChange} accept="image/*" className="hidden" />
//                                 </div>
//                             ) : (
//                                 profileEditForm.photo ? (
//                                     <img src={profileEditForm.photo} alt={profileEditForm.name} className="w-full h-full object-cover" />
//                                 ) : (
//                                     <User className="w-full h-full p-6 text-gray-300 bg-gray-100" />
//                                 )
//                             )}
//                             <div className="absolute bottom-0 w-full bg-black/50 text-[10px] py-1 text-center backdrop-blur-sm">
//                                 {profileEditForm.year}
//                             </div>
//                          </div>
                         
//                          {isEditingProfile ? (
//                              <input 
//                                 name="name" 
//                                 value={profileEditForm.name} 
//                                 onChange={handleProfileEditChange}
//                                 className="text-xl font-bold leading-tight mb-2 text-center bg-white/20 border border-white/30 rounded px-2 py-1 text-white w-full placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white"
//                              />
//                          ) : (
//                              <h2 className="text-xl font-bold leading-tight mb-1">{profileEditForm.name}</h2>
//                          )}

//                          {isEditingProfile ? (
//                              <input 
//                                 name="course" 
//                                 value={profileEditForm.course} 
//                                 onChange={handleProfileEditChange}
//                                 className="text-xs text-indigo-200 font-medium mb-4 text-center bg-white/20 border border-white/30 rounded px-2 py-1 w-full placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white"
//                              />
//                          ) : (
//                              <p className="text-xs text-indigo-200 font-medium mb-4">{profileEditForm.course}</p>
//                          )}
                         
//                          {isEditingProfile ? (
//                              <select 
//                                 name="status" 
//                                 value={profileEditForm.status} 
//                                 onChange={handleProfileEditChange}
//                                 className="px-2 py-1 rounded text-xs font-bold border bg-white/20 border-white/30 text-white w-fit focus:outline-none focus:ring-1 focus:ring-white"
//                              >
//                                  <option value="Verified" className="text-black">Verified</option>
//                                  <option value="Unverified" className="text-black">Unverified</option>
//                              </select>
//                          ) : (
//                              <div className={`px-3 py-1 rounded-full text-xs font-bold border ${profileEditForm.status === 'Verified' ? 'bg-green-500/20 border-green-400 text-green-100' : 'bg-yellow-500/20 border-yellow-400 text-yellow-100'}`}>
//                                  {profileEditForm.status} Status
//                              </div>
//                          )}
//                      </div>

//                      <div className="z-10 w-full mt-8 border-t border-white/20 pt-4">
//                          <div className="flex justify-between items-center text-xs opacity-70 mb-2">
//                              <span>ID Number</span>
//                              <QrCode className="w-4 h-4" />
//                          </div>
//                          <div className="font-mono text-sm tracking-wider bg-black/20 p-2 rounded text-center">
//                              {profileEditForm.studentId}
//                          </div>
//                      </div>
//                  </div>

//                  {/* Right Panel: Academic Dashboard */}
//                  <div className="w-full md:w-2/3 p-8 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
//                      <div className="flex justify-between items-start mb-6">
//                          <div>
//                              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
//                                  <GraduationCap className="w-5 h-5 text-primary" /> Academic Overview
//                              </h3>
//                              <p className="text-xs text-gray-500 dark:text-gray-400">Comprehensive student record</p>
//                          </div>
//                          <button onClick={() => { setViewStudent(null); setIsEditingProfile(false); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
//                              <X className="w-6 h-6" />
//                          </button>
//                      </div>

//                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
//                          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
//                              <div className="flex items-center gap-3 mb-2">
//                                  <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400"><Calendar className="w-4 h-4" /></div>
//                                  <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Year of Entry</span>
//                              </div>
//                              {isEditingProfile ? (
//                                  <input 
//                                     name="year" 
//                                     value={profileEditForm.year} 
//                                     onChange={handleProfileEditChange} 
//                                     className="text-lg font-bold text-gray-900 dark:text-white w-full border-b border-gray-300 dark:border-gray-600 bg-transparent focus:outline-none focus:border-primary"
//                                  />
//                              ) : (
//                                  <p className="text-lg font-bold text-gray-900 dark:text-white pl-1">{profileEditForm.year}</p>
//                              )}
//                          </div>
//                          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
//                              <div className="flex items-center gap-3 mb-2">
//                                  <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-purple-600 dark:text-purple-400"><BookOpen className="w-4 h-4" /></div>
//                                  <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Program</span>
//                              </div>
//                              <p className="text-sm font-bold text-gray-900 dark:text-white pl-1 truncate" title={profileEditForm.course}>{profileEditForm.course}</p>
//                          </div>
//                      </div>

//                      <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
//                         <BadgeCheck className="w-4 h-4" /> Contact Information
//                      </h4>
//                      <div className="space-y-3 mb-8">
//                          <div className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
//                              <Mail className="w-5 h-5 text-gray-400 mr-3" />
//                              <div className="w-full">
//                                  <p className="text-xs text-gray-500 dark:text-gray-400">Email Address</p>
//                                  {isEditingProfile ? (
//                                      <input 
//                                         name="email" 
//                                         value={profileEditForm.email} 
//                                         onChange={handleProfileEditChange}
//                                         className="text-sm font-medium text-gray-900 dark:text-white w-full border-b border-gray-300 dark:border-gray-600 bg-transparent focus:outline-none focus:border-primary"
//                                      />
//                                  ) : (
//                                      <p className="text-sm font-medium text-gray-900 dark:text-white">{profileEditForm.email || 'N/A'}</p>
//                                  )}
//                              </div>
//                          </div>
//                          <div className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
//                              <Phone className="w-5 h-5 text-gray-400 mr-3" />
//                              <div className="w-full">
//                                  <p className="text-xs text-gray-500 dark:text-gray-400">Phone Number</p>
//                                  {isEditingProfile ? (
//                                      <input 
//                                         name="phone" 
//                                         value={profileEditForm.phone} 
//                                         onChange={handleProfileEditChange}
//                                         className="text-sm font-medium text-gray-900 dark:text-white w-full border-b border-gray-300 dark:border-gray-600 bg-transparent focus:outline-none focus:border-primary"
//                                      />
//                                  ) : (
//                                      <p className="text-sm font-medium text-gray-900 dark:text-white">{profileEditForm.phone || 'N/A'}</p>
//                                  )}
//                              </div>
//                          </div>
//                      </div>

//                      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
//                          {isEditingProfile ? (
//                              <>
//                                 <button 
//                                     onClick={() => setIsEditingProfile(false)}
//                                     className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
//                                 >
//                                     Cancel
//                                 </button>
//                                 <button 
//                                     onClick={saveProfileChanges}
//                                     className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-sm flex items-center gap-2"
//                                 >
//                                     <Save className="w-4 h-4" /> Save Changes
//                                 </button>
//                              </>
//                          ) : (
//                              <>
//                                 <button className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
//                                     Download Record
//                                 </button>
//                                 <button 
//                                     onClick={() => setIsEditingProfile(true)}
//                                     className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-indigo-800 transition-colors shadow-sm flex items-center gap-2"
//                                 >
//                                     <Edit className="w-4 h-4" /> Edit Profile
//                                 </button>
//                              </>
//                          )}
//                      </div>
//                  </div>
//              </div>
//         </div>
//         )}

//         {isModalOpen && (
//           <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
//               <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
//                   <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
//                       <div className="flex items-center gap-2"><h3 className="text-xl font-bold text-gray-900 dark:text-white">Add Student</h3></div>
//                       <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"><X className="w-5 h-5" /></button>
//                   </div>
//                   <div className="p-6 space-y-8">
//                        <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
//                           <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-2">To add bulk students you can import the database below</p>
//                           <a href="#" className="text-xs text-primary hover:underline mb-4 block font-semibold">Download Sample CSV File</a>
//                           <button onClick={handleImportClick} className="flex items-center gap-2 px-4 py-2 border border-primary text-primary dark:text-indigo-400 bg-white dark:bg-gray-800 rounded-md text-sm font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors shadow-sm"><FileUp className="w-4 h-4" /> Import CSV File</button>
//                       </div>
//                       <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Input Student Details</h4>
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                            {['First Name', 'Last Name', 'Email', 'Phone Number', 'Course of Study', 'Year of Study'].map(label => {
//                                const name = label.toLowerCase().replace(/\s/g, '').replace('ofstudy', 'OfStudy').replace('firstname', 'firstName').replace('lastname', 'lastName').replace('phonenumber', 'phoneNumber').replace('yearofstudy', 'yearOfStudy');
//                                return (
//                                 <div key={label}>
//                                     <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{label} *</label>
//                                     <input 
//                                         type={label === 'Email' ? 'email' : 'text'} 
//                                         name={name} 
//                                         //@ts-ignore
//                                         value={formData[name]} 
//                                         onChange={handleInputChange} 
//                                         className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-primary focus:border-primary text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400" 
//                                         placeholder={`Enter ${label}`} 
//                                     />
//                                 </div>
//                                );
//                            })}
//                            <div><label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Program Start Date</label><input type="date" name="programStartDate" value={formData.programStartDate} onChange={handleInputChange} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-primary focus:border-primary text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700" /></div>
//                            <div><label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Program Stop Date</label><input type="date" name="programStopDate" value={formData.programStopDate} onChange={handleInputChange} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-primary focus:border-primary text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700" /></div>
//                           <div className="md:col-span-1">
//                               <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Photo *</label>
//                               <div 
//                                 onClick={() => photoInputRef.current?.click()}
//                                 className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors h-32 relative overflow-hidden"
//                               >
//                                   {formData.photo ? (
//                                       <img src={formData.photo} alt="Preview" className="absolute inset-0 w-full h-full object-cover" />
//                                   ) : (
//                                       <>
//                                         <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
//                                         <p className="text-xs text-gray-500 dark:text-gray-400 text-center font-medium">Click to upload photo</p>
//                                       </>
//                                   )}
//                                   <input type="file" ref={photoInputRef} onChange={handlePhotoSelect} accept="image/*" className="hidden" />
//                               </div>
//                           </div>
//                       </div>
//                       <div className="mt-8"><button onClick={handleAddStudent} className="w-fit bg-darkblue text-white px-8 py-3 rounded-lg text-sm font-bold hover:bg-indigo-900 transition-all shadow-md">Add Student</button></div>
//                   </div>
//               </div>
//           </div>
//       )}
//     </div>
//   );
// };
